import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserStatusContext } from '../../context/UserStatus';
import useGetCareManagers from './api/useGetCareManagers';

/**
 * Care Managers see the same list, tabs and filters as Admins, with one
 * difference: their own name is pre-selected in the Care Manager filter by
 * default. This sets `care_manager_ids` to the current user's care-manager id
 * once per list, unless a value is already present in the URL.
 *
 * Per list, not per mount: switching tabs resets the query, so each list seeds
 * the default again while an explicit deselect within a list still sticks.
 */
const useDefaultCareManagerFilter = (isCareManager: boolean) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { userStatus } = useUserStatusContext();
    const { data: careManagers } = useGetCareManagers();

    const listVersion = searchParams.get('list_version') || 'active';
    const initializedFor = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (!isCareManager || initializedFor.current === listVersion) return;
        if (searchParams.get('care_manager_ids')) {
            initializedFor.current = listVersion;
            return;
        }
        const myCareManagerId = careManagers?.find(
            (cm) => cm.hero_user_id === userStatus?.hero_user_id
        )?.id;
        if (myCareManagerId === undefined) return;
        initializedFor.current = listVersion;
        setSearchParams(
            (prev) => {
                prev.set('care_manager_ids', String(myCareManagerId));
                return prev;
            },
            { replace: true }
        );
    }, [careManagers, isCareManager, listVersion, searchParams, setSearchParams, userStatus]);
};

export default useDefaultCareManagerFilter;
